import type { IPDFViewerHandle } from '@/components/Organisms/PDFViewer/PDFViewer';
import useTranslation from '@/hooks/useTranslation';
import { BrowserHttpClient } from '@/services/Http/BrowserHttpClient';
import { logger } from '@/utils/logger/logger';
import { useRouter } from 'next/router';
import { useEffect, useRef, useState } from 'react';
import type { IAttribution, IInsightQAResponse } from './InsightQA';
import { useQAPdfHighlights } from './useQAPdfHighlights';
export type InsightQAState = ReturnType<typeof useInsightQA>;
export function useInsightQA(insightId: string, isOpen: boolean, pdfViewerHandle: IPDFViewerHandle) {
  const {
    query
  } = useRouter();
  const {
    lang
  } = useTranslation();
  const [loadingStartedAt, setLoadingStartedAt] = useState<number | null>(null);
  const [response, setResponse] = useState<IInsightQAResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const {
    clearHighlights,
    scrollToHighlight,
    renderHighlights
  } = useQAPdfHighlights(pdfViewerHandle);
  const isLoading = loadingStartedAt !== null;
  const isOpenRef = useRef(isOpen);
  isOpenRef.current = isOpen;
  useEffect(() => {
    if (!isOpen) clearHighlights();
  }, [isOpen]);
  const handleSubmit = async (question: string) => {
    if (isLoading) return;
    setLoadingStartedAt(Date.now());
    setError(null);
    setResponse(null);
    clearHighlights();
    try {
      const data = await new BrowserHttpClient().post<IInsightQAResponse>({
        path: `/insights/${insightId}/qa`,
        body: {
          question,
          language: query.language || lang
        }
      });
      setResponse(data);
      if (!isOpenRef.current) return;
      renderHighlights(data.answers.flatMap(a => a.attributions).filter(attr => attr.page !== null).map(attr_0 => ({
        page: attr_0.page!,
        matchedText: attr_0.matchedText
      })));
    } catch (err: any) {
      logger.error(`InsightQA: Failed to fetch QA response for insight ${insightId}`, err);
      setError('An unexpected error occurred.');
    } finally {
      setLoadingStartedAt(null);
    }
  };
  const handleAttributionClick = (attr_1: IAttribution) => {
    if (!attr_1.page) return;
    scrollToHighlight({
      page: attr_1.page,
      matchedText: attr_1.matchedText
    });
  };
  return {
    isLoading,
    loadingStartedAt,
    response,
    error,
    handleSubmit,
    handleAttributionClick
  };
}